import React, { useState, useMemo } from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Checkbox } from "../../ui/checkbox";
import { Card, CardContent, CardHeader } from "../../ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import {
  MagnifyingGlass,
  CalendarBlank,
  Hash,
  Briefcase,
  CheckCircle,
  XCircle,
  Eye,
  Warning,
} from "@phosphor-icons/react";
import { Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  useGetManagerPendingAssessments,
  useBulkEndorseAssessments,
} from "../../../adapters/Requests";
import { DateRangeFilter } from "../../ui/daterangefilter";
import { toast } from "sonner";
import { Sheet, SheetContent } from "../../ui/sheet";
import AssessmentReportViewer from "../../viewers/AssessmentReportViewer";

export default function ManagersTable() {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState("PENDING");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [rowSelection, setRowSelection] = useState({});

  const [selectedInternId, setSelectedInternId] = useState(null);

  const bulkEndorseMutation = useBulkEndorseAssessments();

  const months = [
    "JANUARY",
    "FEBRUARY",
    "MARCH",
    "APRIL",
    "MAY",
    "JUNE",
    "JULY",
    "AUGUST",
    "SEPTEMBER",
    "OCTOBER",
    "NOVEMBER",
    "DECEMBER",
  ];

  const monthYearQuery = `${months[selectedMonth]} ${selectedYear}`;

  const {
    data: apiData,
    isLoading,
    isFetching,
    refetch,
  } = useGetManagerPendingAssessments({
    month_year: monthYearQuery,
    status: viewMode,
    search: searchTerm,
    start_date: dateRange.from,
    end_date: dateRange.to,
  });

  const handleRowClick = (id) => {
    if (!hasSelections) {
      setSelectedInternId(id);
    }
  };

  const tableData = useMemo(() => apiData?.data || [], [apiData]);
  const hasSelections = Object.keys(rowSelection).length > 0;

  const handleBulkEndorse = async () => {
    const selectedRows = table.getSelectedRowModel().rows;
    const validRows = selectedRows.filter(
      (row) =>
        !row.original.supervisors_comment?.includes(
          "[URGENT: BANK DETAILS UPDATE REQUESTED]",
        ),
    );

    const selectedIds = validRows.map((row) => row.original.id);
    const selectedNames = validRows.map(
      (row) => row.original.intern.first_name,
    );

    if (selectedIds.length === 0) return;

    try {
      await bulkEndorseMutation.mutateAsync({ assessment_ids: selectedIds });

      let message = "";
      if (selectedNames.length <= 3) {
        message = `${selectedNames.join(", ")} endorsed successfully`;
      } else {
        message = `${selectedNames.length} interns endorsed successfully`;
      }

      toast.success(message);
      setRowSelection({});
      refetch();
    } catch (error) {
      toast.error("Failed to endorse assessments");
      console.error("Bulk endorse failed", error);
    }
  };

  const columns = useMemo(() => {
    const cols = [
      ...(viewMode === "PENDING"
        ? [
          {
            id: "select",
            header: ({ table }) => (
              <Checkbox
                checked={table.getIsAllPageRowsSelected()}
                onCheckedChange={(value) =>
                  table.toggleAllPageRowsSelected(!!value)
                }
                aria-label="Select all"
                className="mx-2"
              />
            ),
            cell: ({ row }) => {
              const hasBankRequest =
                row.original.supervisors_comment?.includes(
                  "[URGENT: BANK DETAILS UPDATE REQUESTED]",
                );

              return (
                <div className="px-2" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={row.getIsSelected()}
                    onCheckedChange={(value) => row.toggleSelected(!!value)}
                    aria-label="Select row"
                    disabled={hasBankRequest}
                    className={
                      hasBankRequest ? "opacity-50 cursor-not-allowed" : ""
                    }
                  />
                </div>
              );
            },
            enableSorting: false,
            enableHiding: false,
          },
        ]
        : []),
      {
        header: "Intern Details",
        accessorKey: "intern",
        cell: ({ row }) => {
          const intern = row.original.intern;
          const hasBankRequest = row.original.supervisors_comment?.includes(
            "[URGENT: BANK DETAILS UPDATE REQUESTED]",
          );

          return (
            <div className="flex flex-col py-1">
              <div className="flex items-center gap-2">
                <span className="font-bold text-gray-900 text-sm">
                  {intern.first_name} {intern.last_name}
                </span>
                {hasBankRequest && (
                  <span
                    className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-orange-100 text-orange-700 border border-orange-200"
                    title="Bank Details Update Requested - Cannot Bulk Endorse"
                  >
                    <Warning weight="fill" className="mr-1" /> BANK UPDATE
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[11px] text-gray-500 font-mono flex items-center bg-gray-50 px-1.5 py-0.5 rounded border border-gray-100">
                  <Hash size={10} className="mr-0.5" />{" "}
                  {intern.application?.code || "N/A"}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        header: "Department",
        accessorKey: "department",
        cell: ({ row }) => (
          <div className="flex items-center text-gray-700 text-xs font-medium">
            <Briefcase className="mr-1.5 text-gray-400" size={14} />
            {row.original.intern.application?.vacancy?.business_unit?.name ||
              "N/A"}
          </div>
        ),
      },
      {
        header: "Supervisor Score",
        accessorKey: "score",
        cell: ({ row }) => (
          <div className="flex flex-col gap-1">
            <div className="text-xs font-semibold text-slate-700">
              Punctuality:{" "}
              <span className="text-green-600">
                {row.original.punctuality_score}/5
              </span>
            </div>
            <div className="text-[10px] text-slate-500 truncate max-w-[200px] italic">
              "
              {row.original.supervisors_comment
                ?.split("[URGENT")[0]
                ?.split("---")[0]
                ?.substring(0, 50)}
              ..."
            </div>
          </div>
        ),
      },
      {
        header: "Status",
        id: "status",
        cell: () => (
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-medium border ${viewMode === "PENDING"
              ? "bg-orange-50 text-orange-700 border-orange-100"
              : "bg-green-50 text-green-700 border-green-100"
              }`}
          >
            {viewMode === "PENDING" ? "Pending" : "Endorsed"}
          </span>
        ),
      },
    ];

    if (!hasSelections) {
      cols.push({
        id: "actions",
        header: "Action",
        cell: ({ row }) =>
          viewMode === "PENDING" ? (
            <Button
              size="sm"
              className="h-9 border border-green-400 bg-white hover:bg-green-100 text-green-500 text-xs px-4"
              onClick={(e) => {
                e.stopPropagation();
                handleRowClick(row.original.intern_id);
              }}
            >
              <CheckCircle size={14} />
              Endorse
            </Button>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              className="h-8 text-xs gap-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100"
              onClick={(e) => {
                e.stopPropagation();
                handleRowClick(row.original.intern_id);
              }}
            >
              <Eye size={14} />
              View
            </Button>
          ),
      });
    }

    return cols;
  }, [viewMode, hasSelections]);

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      rowSelection,
    },
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: (row) =>
      !row.original.supervisors_comment?.includes(
        "[URGENT: BANK DETAILS UPDATE REQUESTED]",
      ),
  });

  return (
    <>
      <Card className="w-full max-w-[95%] m-auto border-slate-200 shadow-sm bg-white mt-4 relative">
        <CardHeader className="flex flex-col gap-4 p-4 border-b border-slate-100 bg-gray-50/40">
          <div className="flex flex-wrap items-center gap-4 border-b border-gray-200 pb-4">
            <button
              onClick={() => {
                setViewMode("PENDING");
                setRowSelection({});
              }}
              className={`text-sm font-medium px-4 py-2 rounded-md transition-colors ${viewMode === "PENDING"
                ? "bg-orange-100 text-orange-800 shadow-sm ring-1 ring-orange-200"
                : "text-gray-500 hover:bg-gray-100"
                }`}
            >
              Pending
            </button>
            <button
              onClick={() => {
                setViewMode("ENDORSED");
                setRowSelection({});
              }}
              className={`text-sm font-medium px-4 py-2 rounded-md transition-colors ${viewMode === "ENDORSED"
                ? "bg-green-100 text-green-800 shadow-sm ring-1 ring-green-200"
                : "text-gray-500 hover:bg-gray-100"
                }`}
            >
              Endorsed
            </button>
          </div>

          <div className="flex flex-col sm:flex-row justify-between gap-4">
            <div className="flex gap-2 items-center flex-wrap">
              <DateRangeFilter
                dateRange={dateRange}
                setDateRange={setDateRange}
                minDate={apiData?.meta?.minDate}
              />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 text-xs border-slate-300 bg-white"
                  >
                    <CalendarBlank className="mr-2 h-4 w-4 text-slate-500" />
                    {months[selectedMonth]} {selectedYear}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="h-64 overflow-y-auto"
                >
                  {months.map((m, i) => (
                    <DropdownMenuItem
                      key={m}
                      onClick={() => setSelectedMonth(i)}
                    >
                      {m}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="relative w-full sm:w-64">
              <MagnifyingGlass
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                size={14}
              />
              <Input
                placeholder={`Search ${viewMode.toLowerCase()} interns...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 h-9 text-xs bg-white border-slate-200 focus:border-green-500 focus:ring-green-500"
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0 relative">
          {isFetching && !isLoading && (
            <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-green-600 animate-spin" />
            </div>
          )}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-gray-50/50">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow
                    key={headerGroup.id}
                    className="border-b border-slate-100"
                  >
                    {headerGroup.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        className="h-10 text-[11px] font-bold text-gray-500 uppercase tracking-wider"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-64 text-center"
                    >
                      <Loader2 className="w-8 h-8 text-green-500 animate-spin mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      className={`border-b border-slate-100 transition-colors cursor-pointer hover:bg-slate-50 ${row.getIsSelected() ? "bg-green-50/50" : ""
                        }`}
                      onClick={() => {
                        if (hasSelections && viewMode === "PENDING") {
                          if (
                            !row.original.supervisors_comment?.includes(
                              "[URGENT: BANK DETAILS UPDATE REQUESTED]",
                            )
                          ) {
                            row.toggleSelected();
                          }
                        } else {
                          handleRowClick(row.original.intern_id);
                        }
                      }}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} className="py-3">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-32 text-center text-slate-500 text-sm"
                    >
                      No {viewMode.toLowerCase()} records found for{" "}
                      {months[selectedMonth]}.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>

        {hasSelections && viewMode === "PENDING" && (
          <div className="sticky bottom-0 bg-green-50 border-t border-green-200 px-4 py-3 flex items-center justify-center z-50 animate-in slide-in-from-bottom-2 fade-in duration-300">
            <div className="flex items-center justify-between w-full max-w-4xl">
              <span className="text-sm font-semibold text-green-900">
                {Object.keys(rowSelection).length} application
                {Object.keys(rowSelection).length !== 1 ? "s" : ""} selected
              </span>
              <div className="flex items-center gap-3">
                <Button
                  size="sm"
                  className="h-9 border border-green-600 bg-green-600 hover:bg-green-700 text-white text-xs px-6 shadow-md"
                  onClick={handleBulkEndorse}
                  disabled={bulkEndorseMutation.isPending}
                >
                  {bulkEndorseMutation.isPending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" />
                  ) : (
                    <CheckCircle className="w-3.5 h-3.5 mr-2" />
                  )}
                  Endorse Selected
                </Button>

                <Button
                  size="sm"
                  variant="ghost"
                  className="h-9 text-xs text-green-700 hover:text-green-900 hover:bg-green-100 px-4 flex items-center gap-1"
                  onClick={() => setRowSelection({})}
                >
                  <XCircle size={16} />
                  Clear Selection
                </Button>
              </div>
            </div>
          </div>
        )}
      </Card>



      <Sheet
        open={!!selectedInternId}
        onOpenChange={(open) => !open && setSelectedInternId(null)}
      >
        <SheetContent side="right" className="sm:max-w-[65%] w-full">
          {selectedInternId && (
            <AssessmentReportViewer
              internId={selectedInternId}
              onClose={() => {
                setSelectedInternId(null);
                refetch();
              }}
            />
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
